// src/blogs/InfiniteBlogFeed.jsx
// Generic infinite-scroll list over a static, already-filtered/ordered post
// array. Appends more posts as a sentinel nears the viewport, and can jump to
// (revealing + scrolling to) a specific post on demand — used by the sidebar's
// date list, the movie poster grid, edge-click prev/next paging, and post
// links / permalinks (see postLinks.js).
//
// A jump is a `jumpRequest` object, { id, instant? } — an object rather than
// a bare id so that asking for the same post twice in a row (scroll away,
// click the link again) is a new request and scrolls again. `instant` skips
// the smooth-scroll animation, for arriving on a deep link.
import React, { useEffect, useMemo, useRef, useState } from "react";

import { postDomId } from "./postLinks.js";

const PAGE_SIZE = 6;
const PRELOAD_MARGIN = "0px 0px 400px 0px";

export default function InfiniteBlogFeed({
  posts,
  renderItem,
  jumpRequest = null,
  onActivePost,
  pageSize = PAGE_SIZE,
  emptyLabel = "posts",
}) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [trackedPosts, setTrackedPosts] = useState(posts);
  const sentinelRef = useRef(null);
  const listRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const handledRequestRef = useRef(null);

  // A filter change swaps `posts`' identity — start that view back at page
  // one rather than implying more was appended than the filtered set has.
  // Adjusted during render (React's own recommended pattern for resetting
  // state when a prop's identity changes) rather than in an effect, so the
  // reset lands in the same commit as the new list.
  if (posts !== trackedPosts) {
    setTrackedPosts(posts);
    setVisibleCount(pageSize);
  }

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setVisibleCount((count) => Math.min(count + pageSize, posts.length));
      },
      { rootMargin: PRELOAD_MARGIN }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [posts.length, pageSize]);

  // Tracks which post is "mainly being viewed" — the one under a thin band
  // 35% of the way down the viewport — so edge-click prev/next has a current
  // post to step from and per-post themes know which one to apply. A band
  // (rather than "closest to the top edge") keeps a tall post active for as
  // long as it fills the screen, however far it's scrolled: the observer
  // fires exactly when a post starts or stops covering the band. On each
  // firing the answer is recomputed from every post — the one covering the
  // line, or when it falls in a gap between posts (or a short post doesn't
  // reach it) the nearest one — so there's always exactly one active post.
  useEffect(() => {
    const list = listRef.current;
    if (!list || !onActivePost) return undefined;

    const observer = new IntersectionObserver(
      () => {
        const line = window.innerHeight * 0.35;
        let best = null;
        let bestDistance = Infinity;
        for (const card of list.querySelectorAll("[data-post-id]")) {
          const { top, bottom } = card.getBoundingClientRect();
          const distance =
            top <= line && bottom >= line ? 0 : Math.min(Math.abs(top - line), Math.abs(bottom - line));
          if (distance < bestDistance) {
            bestDistance = distance;
            best = card.dataset.postId;
          }
        }
        if (best) onActivePost(best);
      },
      { rootMargin: "-35% 0px -64% 0px", threshold: 0 }
    );

    const cards = list.querySelectorAll("[data-post-id]");
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [posts, visibleCount, onActivePost]);

  // Marks a jump as pending (below) rather than depending on `visibleCount`
  // directly, so a later, unrelated infinite-scroll append doesn't re-fire
  // the scroll-into-view effect and yank the page back to a stale target.
  //
  // A request whose post isn't in `posts` yet is left unhandled, so it can
  // still land if the list changes (a filter being cleared to reveal it). Once
  // handled it's never re-run, so a later filter change can't drag the page
  // back to a stale target.
  useEffect(() => {
    if (!jumpRequest || handledRequestRef.current === jumpRequest) return;
    const index = posts.findIndex((post) => post.id === jumpRequest.id);
    if (index === -1) return;

    handledRequestRef.current = jumpRequest;
    pendingScrollRef.current = jumpRequest;
    if (index >= visibleCount) {
      setVisibleCount(Math.min(index + pageSize, posts.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpRequest, posts, pageSize]);

  useEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    const el = document.getElementById(postDomId(pending.id));
    if (!el) return;
    el.scrollIntoView({ behavior: pending.instant ? "auto" : "smooth", block: "start" });
    pendingScrollRef.current = null;
  }, [jumpRequest, visibleCount]);

  const visiblePosts = useMemo(() => posts.slice(0, visibleCount), [posts, visibleCount]);

  return (
    <div className="infinite-feed" ref={listRef}>
      {visiblePosts.length === 0 ? (
        <div className="infinite-feed__empty">No {emptyLabel} match the current filter.</div>
      ) : (
        visiblePosts.map((post) => (
          <div key={post.id} data-post-id={post.id} id={postDomId(post.id)}>
            {renderItem(post)}
          </div>
        ))
      )}
      <div ref={sentinelRef} className="infinite-feed__sentinel" aria-hidden="true" />
    </div>
  );
}
